import React from 'react';
import {
  ContentType,
  formatMinutes,
  InformationCard,
  InformationCardState,
  useDeviceSize,
} from '@atlassian/learning-components';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { useLoaderData } from 'react-router-dom';

import { getDuration } from 'src/pages/OldLearningPathPage/children/utils/learningPathUtils';
import { useSaveContent } from 'src/hooks/useSaveContent';
import { useAtlassianUser } from 'src/hooks/useAtlassianUser';
import { getOptions } from 'src/pages/LessonPage/children/utils/getOptions';
import { LearningPathLoaderProps } from 'src/pages/OldLearningPathPage';
import { shortenDurationString } from 'src/pages/LearningPathPage/utils';

interface InformationCardWrapperProps {
  urlToShare: string;
}

export const InformationCardWrapper: React.FC<InformationCardWrapperProps> = ({ urlToShare }) => {
  const { learningPath } = useLoaderData() as LearningPathLoaderProps;
  const { user, isLoading } = useAtlassianUser();
  const { isContentSaved, saveContent } = useSaveContent({
    contentId: learningPath?.sys.id,
    contentType: ContentType.LEARNING_PATH,
    learningPathId: learningPath?.sys.id,
    user,
  });
  const deviceWidth = useDeviceSize();
  const summary = learningPath?.learningPathSummary?.json
    ? documentToReactComponents(
      learningPath.learningPathSummary.json,
      getOptions(learningPath.learningPathSummary, deviceWidth),
    )
    : '';

  const learningPathDuration = shortenDurationString(
    formatMinutes(learningPath?.coursesCollection.items.reduce((acc, course) => acc + getDuration(course), 0)),
  );

  const numberOfCompletedCourses = 0;

  const calculateProgress = (): number =>
    (numberOfCompletedCourses / learningPath.coursesCollection.items.length) * 100;

  const getCourseState = (): InformationCardState => {
    if (isLoading) {
      return InformationCardState.LOADING;
    }

    if (!user) {
      return InformationCardState.DEFAULT;
    }

    return InformationCardState.DEFAULT;
  };

  return (
    <InformationCard
      data-testid="information-card"
      contentType={ContentType.LEARNING_PATH}
      progress={calculateProgress()}
      duration={learningPathDuration}
      description={summary}
      state={getCourseState()}
      isSaved={isContentSaved}
      onSave={saveContent}
      urlToShare={urlToShare}
    />
  );
};
